"use client";

import { CrayonChicky } from "@/components/mascots/CrayonChicky";
import type { SessionAgentState } from "@/shared/lesson/view-contracts";
import { motion, useReducedMotion } from "framer-motion";

interface LoadingChickyProps {
  agentState?: SessionAgentState | string;
  interactive?: boolean;
  className?: string;
}

const STATE_VISUALS: Record<
  SessionAgentState,
  { glowPeak: number; fluidOpacity: number; fluidScale: number }
> = {
  connecting: { glowPeak: 0.2, fluidOpacity: 0.45, fluidScale: 1.035 },
  thinking: { glowPeak: 0.24, fluidOpacity: 0.5, fluidScale: 1.05 },
  speaking: { glowPeak: 0.26, fluidOpacity: 0.52, fluidScale: 1.06 },
  listening: { glowPeak: 0.17, fluidOpacity: 0.4, fluidScale: 1.025 },
  idle: { glowPeak: 0.16, fluidOpacity: 0.38, fluidScale: 1.02 },
  disconnected: { glowPeak: 0.08, fluidOpacity: 0.2, fluidScale: 1 },
  error: { glowPeak: 0.08, fluidOpacity: 0.2, fluidScale: 1 },
};

function normalizeAgentState(agentState: LoadingChickyProps["agentState"]): SessionAgentState {
  if (agentState && agentState in STATE_VISUALS) return agentState as SessionAgentState;
  return "idle";
}

export function LoadingChicky({
  agentState = "idle",
  interactive = true,
  className = "",
}: LoadingChickyProps) {
  const state = normalizeAgentState(agentState);
  const reduceMotion = useReducedMotion();
  const visuals = STATE_VISUALS[state];
  const subdued = state === "disconnected" || state === "error";

  return (
    <div
      className={`relative flex select-none flex-col items-center ${className}`}
      data-testid="loading-chicky"
    >
      <div className="relative flex h-24 w-24 items-center justify-center">
        <div
          aria-hidden="true"
          className={`pointer-events-none absolute inset-0 rounded-full border-[3px] border-b-transparent border-l-transparent transition-colors duration-500 motion-safe:animate-spin ${
            subdued
              ? "border-r-orange-300/25 border-t-orange-400/35"
              : "border-r-orange-400/50 border-t-amber-400/60"
          }`}
          data-testid="loading-chicky-ring"
          style={{ animationDuration: "2.8s" }}
        />

        <motion.div
          aria-hidden="true"
          animate={
            reduceMotion
              ? { opacity: Math.min(visuals.glowPeak, 0.12) }
              : {
                  opacity: [0.08, visuals.glowPeak, 0.08],
                  scale: [1, 1.15, 1],
                }
          }
          className="pointer-events-none absolute -inset-1 rounded-full bg-gradient-to-r from-orange-400 to-amber-400 blur-xl"
          transition={{ duration: 3, ease: "easeInOut", repeat: Infinity }}
        />

        <motion.div
          aria-hidden="true"
          animate={
            reduceMotion
              ? { opacity: visuals.fluidOpacity }
              : {
                  borderRadius: [
                    "50% 50% 50% 50%",
                    "60% 40% 68% 32%",
                    "34% 66% 42% 58%",
                    "50% 50% 50% 50%",
                  ],
                  opacity: visuals.fluidOpacity,
                  rotate: [0, 90, 180, 360],
                  scale: [1, visuals.fluidScale, 0.94, 1],
                }
          }
          className="pointer-events-none absolute h-16 w-16 rounded-full bg-gradient-to-br from-orange-400 to-red-500 blur-md"
          transition={{ duration: 6, ease: "linear", repeat: Infinity }}
        />

        <motion.div
          aria-hidden="true"
          animate={
            reduceMotion
              ? undefined
              : {
                  borderRadius: [
                    "42% 58% 60% 40%",
                    "50% 50% 50% 50%",
                    "60% 40% 34% 66%",
                    "42% 58% 60% 40%",
                  ],
                  rotate: [360, 180, 0],
                  scale: [1.08, 0.92, 1.08],
                }
          }
          className="pointer-events-none absolute h-14 w-14 rounded-full bg-gradient-to-tr from-amber-300 to-orange-400 opacity-25 mix-blend-overlay blur-sm"
          transition={{ duration: 5, ease: "easeInOut", repeat: Infinity }}
        />

        <motion.div
          aria-hidden="true"
          animate={reduceMotion ? undefined : { scale: [1, 1.035, 1] }}
          className="pointer-events-none absolute z-[5] h-14 w-14 rounded-full bg-white/85 shadow-[0_0_15px_rgba(251,146,60,0.45)] backdrop-blur-sm"
          transition={{ duration: 1.6, ease: "easeInOut", repeat: Infinity }}
        />

        <div
          aria-hidden="true"
          className="pointer-events-none absolute z-[6] h-9 w-9 rounded-full bg-gradient-to-br from-orange-50 to-orange-100/60"
          data-testid="loading-chicky-inner-disc"
        />

        <div className="absolute inset-4 z-10 overflow-hidden rounded-full">
          <CrayonChicky
            agentState={state}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 scale-[0.32] opacity-[0.34]"
            interactive={interactive}
            showHint={false}
          />
        </div>
      </div>

      <span className="sr-only">Preparing your tutor</span>
    </div>
  );
}
